<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold">Gestión de Precios</h1>
      <div class="flex space-x-2">
        <Link href="/products" class="btn-gray">Volver a Productos</Link>
      </div>
    </div>

    <Head title="Gestión de Precios" />

    <!-- Filtros y Búsqueda -->
    <div class="bg-white rounded-lg shadow-md mb-6 p-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <text-input
          v-model="form.search.terms"
          label="Buscar por nombre, código o descripción"
          placeholder="Ingrese términos de búsqueda"
        />
        <select-input v-model="form.search.brand_id" label="Marca">
          <option value="">Todas las marcas</option>
          <option v-for="brand in brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
        </select-input>
        <select-input v-model="form.search.category" label="Categoría">
          <option value="">Todas las categorías</option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
        </select-input>
      </div>
    </div>

    <!-- Bulk Actions Panel -->
    <div class="bg-teal-50 border border-teal-200 rounded-lg shadow-md mb-6 p-6" v-if="selected.length > 0">
      <h3 class="text-lg font-semibold text-teal-800 mb-4">Acciones Masivas ({{ selected.length }} seleccionados)</h3>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label class="form-label">Valor</label>
          <input type="number" v-model="bulkForm.value" class="form-input w-full" step="0.01" min="0">
        </div>
        <div>
          <label class="form-label">Tipo</label>
          <select v-model="bulkForm.type" class="form-select w-full">
            <option value="percentage">Porcentaje (%)</option>
            <option value="amount">Monto Fijo ($)</option>
          </select>
        </div>
        <div class="flex space-x-2">
          <button @click="applyBulk('up')" class="btn-blue flex-1" :disabled="bulkForm.processing">
            <i class="fa fa-arrow-up mr-2"></i> Subir
          </button>
          <button @click="applyBulk('down')" class="btn-red flex-1" :disabled="bulkForm.processing">
            <i class="fa fa-arrow-down mr-2"></i> Bajar
          </button>
        </div>
      </div>
      
      <div class="mt-4">
        <p class="text-sm font-semibold text-gray-700 mb-2">Campos a actualizar:</p>
        <div class="flex flex-wrap gap-4">
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="bulkForm.prices_to_update" value="cost" class="form-checkbox text-orange-600">
            <span class="ml-2 font-bold text-orange-700">Costo</span>
          </label>
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="bulkForm.prices_to_update" value="price" class="form-checkbox">
            <span class="ml-2">Precio A</span>
          </label>
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="bulkForm.prices_to_update" value="priceB" class="form-checkbox">
            <span class="ml-2">Precio B</span>
          </label>
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="bulkForm.prices_to_update" value="priceC" class="form-checkbox">
            <span class="ml-2">Precio C</span>
          </label>
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="bulkForm.prices_to_update" value="priceD" class="form-checkbox">
            <span class="ml-2">Precio D</span>
          </label>
          <label v-for="group in groups" :key="group.id" class="inline-flex items-center">
            <input type="checkbox" v-model="bulkForm.prices_to_update" :value="`group_${group.id}`" class="form-checkbox text-teal-600">
            <span class="ml-2">Grupo: {{ group.name }}</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Inventory Table -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50 border-b">
              <th class="py-3 px-6">
                <input type="checkbox" @change="toggleAll" :checked="allSelected" class="form-checkbox">
              </th>
              <th class="py-3 px-6 font-semibold">Producto</th>
              <th class="py-3 px-6 font-semibold">Costo</th>
              <th class="py-3 px-6 font-semibold">Precio A</th>
              <th class="py-3 px-6 font-semibold">Precio B</th>
              <th class="py-3 px-6 font-semibold">Precio C</th>
              <th class="py-3 px-6 font-semibold">Precio D</th>
              <th v-for="group in groups" :key="group.id" class="py-3 px-6 font-semibold">
                {{ group.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in products.data" :key="product.id" class="hover:bg-gray-50 border-b">
              <td class="py-3 px-6 text-center">
                <input type="checkbox" v-model="selected" :value="product.id" class="form-checkbox">
              </td>
              <td class="py-3 px-6">
                <div class="font-medium text-gray-900">{{ product.name }}</div>
                <div class="text-xs text-gray-500">{{ product.code }}</div>
              </td>
              <!-- Costo -->
              <td class="py-3 px-6">
                <div class="flex items-center">
                  <span class="mr-1 text-gray-400">$</span>
                  <input type="number" v-model="product.cost" step="0.01" class="w-24 border-gray-300 rounded text-sm text-right focus:ring-teal-500 focus:border-teal-500" @change="markAsDirty(product)">
                </div>
              </td>
              <!-- Precios Propios -->
              <td class="py-3 px-6">
                <div class="flex items-center">
                  <span class="mr-1 text-gray-400">$</span>
                  <input type="number" v-model="product.price" step="0.01" class="w-24 border-gray-300 rounded text-sm text-right focus:ring-teal-500 focus:border-teal-500" @change="markAsDirty(product)">
                </div>
              </td>
              <td class="py-3 px-6">
                <div class="flex items-center">
                  <span class="mr-1 text-gray-400">$</span>
                  <input type="number" v-model="product.priceB" step="0.01" class="w-24 border-gray-300 rounded text-sm text-right focus:ring-teal-500 focus:border-teal-500" @change="markAsDirty(product)">
                </div>
              </td>
              <td class="py-3 px-6">
                <div class="flex items-center">
                  <span class="mr-1 text-gray-400">$</span>
                  <input type="number" v-model="product.priceC" step="0.01" class="w-24 border-gray-300 rounded text-sm text-right focus:ring-teal-500 focus:border-teal-500" @change="markAsDirty(product)">
                </div>
              </td>
              <td class="py-3 px-6">
                <div class="flex items-center">
                  <span class="mr-1 text-gray-400">$</span>
                  <input type="number" v-model="product.priceD" step="0.01" class="w-24 border-gray-300 rounded text-sm text-right focus:ring-teal-500 focus:border-teal-500" @change="markAsDirty(product)">
                </div>
              </td>
              <!-- Precios de Grupo -->
              <td v-for="group in groups" :key="group.id" class="py-3 px-6 text-blue-700 font-bold">
                <div class="flex items-center">
                  <span class="mr-1 text-gray-400">$</span>
                  <input type="number" :value="getGroupPrice(product, group.id)" @input="setGroupPrice(product, group.id, $event.target.value)" step="0.01" class="w-24 border-gray-300 rounded text-sm text-right focus:ring-blue-500 focus:border-blue-500" @change="markAsDirty(product)">
                </div>
              </td>
            </tr>
            <tr v-if="!products.data.length">
              <td :colspan="7 + groups.length" class="py-10 text-center text-gray-500">
                No se encontraron productos.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <div class="mt-6 flex justify-between items-center">
      <pagination :links="products.links" />
      <button v-if="dirtyProducts.size > 0" @click="saveChanges" class="btn-teal flex items-center gap-2 px-8 py-3 shadow-lg">
        <i class="fa fa-save"></i>
        <span>Guardar cambios individuales ({{ dirtyProducts.size }})</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import Pagination from '@/Shared/Pagination'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    Pagination,
  },
  layout: Layout,
  props: {
    products: Object,
    filters: Object,
    brands: Array,
    categories: Array,
    groups: Array,
    change: [Number, String],
  },
  data() {
    return {
      selected: [],
      dirtyProducts: new Set(),
      form: {
        search: {
          terms: this.filters.search?.terms || '',
          brand_id: this.filters.search?.brand_id || '',
          category: this.filters.search?.category || '',
        },
      },
      bulkForm: this.$inertia.form({
        product_ids: [],
        type: 'percentage',
        direction: 'up',
        value: 0,
        prices_to_update: ['price', 'priceB', 'priceC', 'priceD'],
      }),
    }
  },
  watch: {
    'form.search': {
      handler: throttle(function () {
        this.$inertia.get('/products/prices', pickBy(this.form), {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        })
      }, 300),
      deep: true,
    },
  },
  computed: {
    allSelected() {
      return this.products.data.length > 0 && this.selected.length === this.products.data.length
    },
  },
  methods: {
    toggleAll(e) {
      if (e.target.checked) {
        this.selected = this.products.data.map(p => p.id)
      } else {
        this.selected = []
      }
    },
    getGroupPrice(product, groupId) {
      const gPrice = product.prices?.find(p => p.group_id === groupId)
      return gPrice ? gPrice.price : 0
    },
    setGroupPrice(product, groupId, value) {
      if (!product.prices) product.prices = []
      let gPrice = product.prices.find(p => p.group_id === groupId)
      if (!gPrice) {
        gPrice = { group_id: groupId, price: 0, product_id: product.id }
        product.prices.push(gPrice)
      }
      gPrice.price = value
      this.markAsDirty(product)
    },
    markAsDirty(product) {
      this.dirtyProducts.add(product.id)
    },
    applyBulk(direction) {
      if (this.bulkForm.value <= 0) {
        alert('Debe ingresar un valor mayor a 0.')
        return
      }
      if (this.bulkForm.prices_to_update.length === 0) {
        alert('Debe seleccionar al menos un campo de precio para actualizar.')
        return
      }
      
      if (!confirm(`¿Está seguro de querer ${direction === 'up' ? 'subir' : 'bajar'} los precios seleccionados?`)) {
        return
      }

      this.bulkForm.direction = direction
      this.bulkForm.product_ids = this.selected
      this.bulkForm.post('/products/prices/update', {
        onSuccess: () => {
          this.selected = []
          this.bulkForm.value = 0
          this.dirtyProducts.clear()
        },
      })
    },
    saveChanges() {
      const modified = this.products.data.filter(p => this.dirtyProducts.has(p.id))
      const form = this.$inertia.form({
        products: modified.map(p => ({
          id: p.id,
          cost: p.cost,
          price: p.price,
          priceB: p.priceB,
          priceC: p.priceC,
          priceD: p.priceD,
          group_prices: p.prices.map(gp => ({
            group_id: gp.group_id,
            price: gp.price
          }))
        }))
      })

      form.post('/products/prices/batch-update', {
        onSuccess: () => {
          this.dirtyProducts.clear()
        }
      })
    }
  },
}
</script>

<style scoped>
.btn-teal {
  background-color: #0d9488;
  color: white;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
}
.btn-teal:hover {
  background-color: #0f766e;
}
</style>
